<template>
  <fts-design-modal
    width="1280px"
    append-content-class="dm-stepper"
    class="fts-activity-design-modal"
    ref="ftsDesignModal"
  >
    <!-- title -->
    <template #title>
      <div class="design-modal-header-std__title with-hint">
        <fts-emoji v-model="formData.emoji" :toggle-size="18" class="design-modal-header-emoji" />
        <input
          type="text"
          placeholder="Please enter a name"
          class="fts-form-control design-modal-header-input"
          :class="{error: $v.formData.name && $v.formData.name.$error}"
          :style="getStyle"
          v-model="formData.name"
          spellcheck="false"
          @focus="designNameHasFocus = true"
          @blur="designNameHasFocus = false"
          @input="dispatchAutofill"
          @keydown.enter="doneContinue"
          ref="designerName"
        />
        <div class="design-modal-header-hint" v-if="headerHintHelper">press enter to continue</div>
        <!-- color -->
        <swatches
          class="design-modal-header-swatches"
          v-model="formData.color"
          row-length="7"
          :swatches="colors"
          shapes="circles"
          popover-x="right"
          :swatch-size="25"
          background-color="var(--fts-popover-alt-background)"
          @input="selectColor"
        />
      </div>
      <!-- error message -->
      <div class="design-modal-form-error" v-if="error('name')">{{error('name')}}</div>
    </template>
    <!-- description -->
    <template #description>
      <span class="design-modal-header-tag">Activity</span>
      <span class="design-modal-header-description">designing an activity can be performed by user</span>
    </template>
    <!-- body -->
    <template #body>
      <!-- section nav (§3.2b ledger text; replaces the bottom stepper) -->
      <div class="dm-nav-ledger">
        <template v-for="(tab, ti) in navSections">
          <span class="dm-nav-ledger-sep" v-if="ti" :key="tab.key + '-sep'">·</span>
          <span
            class="dm-nav-ledger-item"
            :class="{ 'dm-nav-ledger-item--on': step === tab.key }"
            @click="setStep(tab.key)"
            :key="tab.key"
          >{{ tab.label }}<sup class="dm-nav-ledger-beta" v-if="tab.beta">beta</sup></span>
        </template>
      </div>
      <!-- content -->
      <div class="dm-stepper-content">
        <!-- description -->
        <div class="design-modal-form-group">
          <fts-contenteditable
            v-model="formData.description"
            text-wrap
            placeholder="enter description (optional)"
          />
        </div>
        <!-- form -->
        <div v-if="step === 'form'">
          <!-- form -->
          <fts-form-designer
            :modules="modules"
            :activity-id="formData.id"
            :form="formData.design"
            :activity="formData"
            :is-form-view="!isCapableWrite"
            :is-capable-add-information="isCapableAddInformation"
            @form:delete="deleteFormDesign"
            @save="done(true, true)"
            v-if="formData.design"
          />
          <!-- form:create -->
          <div class="dm-form-design-create" @click="createWorkDesign" v-else>
            <!-- click to add form -->
            <i class="fts-icon-form dm-form-design-create-icon"></i>
            <div class="dm-form-design-create-plus">
              <font-awesome-icon :icon="['fas', 'plus']" />
            </div>
          </div>
        </div>
        <!-- form automation -->
        <div v-else-if="step === 'automation' && formData.design">
          <fts-form-automation-section
            :form="formData.design"
            :activity-id="formData.id"
            save-button
            @save="done(true, true)"
          />
        </div>
        <!-- script (beta) -->
        <div v-else-if="step === 'script' && beta && formData.design">
          <fts-release-badge class="fts-float-right" />
          <fts-form-script
            :form-data="formData.design"
            :information="information"
            :save-button="!!saveCallBack"
            @save="done(true, true)"
          />
        </div>
        <!-- AI: how the TeamMate performs this activity — standard fields, no popover
             (ruling 2026-08-11). confidence_threshold stays a 0-1 fraction in the payload;
             the field speaks percent. -->
        <div v-else-if="step === 'ai'">
          <div class="fts-row gutters">
            <div class="fts-col fts-col-md-6">
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'actor',
                  displayName: 'AI TeamMate actor',
                  icon: 'fts-icon-user',
                  helper: 'Who performs this activity when it is requested through the AI TeamMate. AI performs it directly; Hybrid asks the user to confirm; Human opens the form for the user.'
                }"
                comfort-design
              >
                <select
                  slot="formElement"
                  slot-scope="{ formData }"
                  v-model="formData.actor"
                  class="fts-form-control"
                >
                  <option value="">Default</option>
                  <option value="ai">AI — performs it directly</option>
                  <option value="hybrid">Hybrid — the user confirms</option>
                  <option value="human">Human — opens the form for the user</option>
                </select>
              </fts-form-element>
            </div>
            <div class="fts-col fts-col-md-6">
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'confidence_threshold',
                  displayName: 'Confidence threshold (%)',
                  helper: 'Below this confidence the TeamMate asks instead of acting. Leave empty for no threshold.'
                }"
                comfort-design
              >
                <input
                  slot="formElement"
                  type="number"
                  min="1"
                  max="100"
                  step="1"
                  v-model.number="aiConfidencePercent"
                  class="fts-form-control"
                  placeholder="e.g. 80"
                >
              </fts-form-element>
            </div>
            <div class="fts-col">
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'ai_instruction',
                  displayName: 'Instruction',
                  helper: 'What the TeamMate should do when it performs this activity.'
                }"
                comfort-design
              >
                <textarea
                  slot="formElement"
                  slot-scope="{ formData }"
                  v-model="formData.ai_instruction"
                  class="fts-form-control"
                  rows="3"
                ></textarea>
              </fts-form-element>
            </div>
            <div class="fts-col">
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'ai_hint',
                  displayName: 'Hint',
                  helper: 'Extra guidance the TeamMate should keep in mind.'
                }"
                comfort-design
              >
                <textarea
                  slot="formElement"
                  slot-scope="{ formData }"
                  v-model="formData.ai_hint"
                  class="fts-form-control"
                  rows="2"
                ></textarea>
              </fts-form-element>
            </div>
          </div>
          <!-- §4.3.3 form-level GNey actions (internal alpha) -->
          <template v-if="isInternal && formData.design">
            <div class="dm-group-label">
              GNey actions <fts-release-badge alpha class="fts-align-top" />
            </div>
            <div class="ai-actions-hint">
              Buttons shown on this form. Clicking one asks GNey to fill or rewrite fields per
              the instruction — the user sees what changed and can undo. Costs AI credits per
              click.
            </div>
            <div
              class="ai-actions-row"
              v-for="action in aiActions"
              :key="action.id"
            >
              <input
                type="text"
                class="fts-form-control ai-actions-row__label"
                placeholder="Button label"
                v-model="action.label"
              />
              <textarea
                class="fts-form-control ai-actions-row__instruction"
                rows="2"
                placeholder="e.g. Summarize the description into the summary field"
                v-model="action.instruction"
              ></textarea>
              <select
                class="fts-form-control ai-actions-row__seniority"
                v-model="action.seniority"
              >
                <option value="junior">Junior</option>
                <option value="senior">Senior</option>
                <option value="expert">Expert</option>
                <option value="consultant">Consultant</option>
              </select>
              <fts-btn icon text @click="removeAiAction(action)">
                <font-awesome-icon :icon="['far', 'trash-alt']" />
              </fts-btn>
            </div>
            <fts-btn sm outline color="primary" text-transform-init @click="addAiAction">
              <font-awesome-icon :icon="['fas', 'plus']" /> Add GNey action
            </fts-btn>
          </template>
        </div>
        <!-- flow -->
        <!-- <div v-else-if="step === 'flow' && isCapableWorkflow">

          <div class="dm-stepper-control">
            <div class="dm-stepper-description">
              <div>User creates an entry by submitting form above. Indicate below whether the entry has an inistate everytime an new entry created. You may always change the configuration in a later time.</div>
              <div>Hint: For entry that does not have a process, you may safely ignore. If it is a workflow or process, you may start by creating the initial state.</div>
            </div>

            <div class="dm-stepper-control-outer" v-if="!builder">
              <fts-btn
                color="primary"
                sm
                outline
                rounded
                text-transform-init
                @click="openWorkflow"
              >Show Diagram</fts-btn>
            </div>
          </div>

          <template v-if="activityFlows && activityFlows.length">
            <div v-for="x in activityFlows" :key="x.id" class="fts-flow-display dm-flow-display">
              <div class="dm-flow-wrapper">

                <fts-state-design-inline
                  v-model="x.fromStateId"
                  :list="states"
                  @state:selected="setFlow(x)"
                  @state:created="setFlow(x)"
                  @navigate="navigateStateDesign"
                  @navigate:activity="navigateNewActivityDesign"
                />

                <font-awesome-icon
                  :icon="['fas', 'right-long']"
                  class="dm-flow-display-direction"
                />

                <fts-label-group
                  :item="{
                    emoji: formData.emoji,
                    name: formData.name,
                    color: formData.color
                  }"
                  emoji
                  label-color
                />

                <font-awesome-icon
                  :icon="['fas', 'right-long']"
                  class="dm-flow-display-direction"
                />

                <template v-if="x.conditional">
                  <template v-for="(state, stateIndex) in x.conditionalStates">
                    <div
                      :key="`or-${state.id}`"
                      v-if="stateIndex"
                      class="dm-flow-display-direction"
                    >or</div>
                    <fts-state-design-inline
                      :key="`state-${state.id}`"
                      v-model="state.toStateId"
                      :list="states"
                      @state:selected="setFlow(x)"
                      @state:created="setFlow(x)"
                      @navigate="navigateStateDesign"
                      @navigate:activity="navigateNewActivityDesign"
                      v-if="state.toStateId"
                    />
                  </template>
                </template>
                <fts-state-design-inline
                  v-model="x.toStateId"
                  :list="states"
                  @state:selected="setFlow(x)"
                  @state:created="setFlow(x)"
                  @navigate="navigateStateDesign"
                  @navigate:activity="navigateNewActivityDesign"
                  v-else
                />
              </div>
              <div class="dm-flow-action">
                <fts-btn icon text @click="removeFlow(x)">
                  <font-awesome-icon :icon="['far', 'trash-alt']" />
                </fts-btn>
              </div>
            </div>
          </template>

          <div class="fts-flow-display dm-flow-display" v-if="flowFormData && flowFormData.id">
            <div class="dm-flow-wrapper">

              <fts-state-design-inline
                :state-id="flowFormData.fromStateId"
                :list="states"
                :editable="false"
                @navigate="navigateStateDesign"
                @navigate:activity="navigateNewActivityDesign"
                v-if="params && params.fromStateId"
              />
              <fts-state-design-inline
                v-model="flowFormData.fromStateId"
                :list="states"
                initial-edit-mode
                @navigate="navigateStateDesign"
                @navigate:activity="navigateNewActivityDesign"
                :key="`from-state-${flowFormData.id}`"
                v-else
              />

              <font-awesome-icon
                :icon="['fas', 'right-long']"
                class="dm-flow-display-direction"
              />

              <fts-label-group
                :item="{
                  emoji: formData.emoji,
                  name: formData.name || ('(activity unnamed)'),
                  color: formData.color
                }"
                emoji
                label-color
                :class="{ 'fts-text-default': !formData.name }"
              />

              <font-awesome-icon
                :icon="['fas', 'right-long']"
                class="dm-flow-display-direction"
              />

              <fts-state-design-inline
                v-model="flowFormData.toStateId"
                :list="states"
                initial-edit-mode
                @navigate="navigateStateDesign"
                @navigate:activity="navigateNewActivityDesign"
                :key="`to-state-${flowFormData.id}`"
              />

              <div
                class="dm-flow-display-error"
                v-if="$v.flowFormData.$error"
              >From State and To State is required.</div>
            </div>
            <div class="dm-flow-action">
              <fts-btn icon text @click="clearFlow">
                <font-awesome-icon :icon="['far', 'trash-alt']" />
              </fts-btn>
            </div>
          </div>

          <div class="fts-text-right" v-if="isExist || !flowFormData">
            <fts-btn color="primary" outline text-transform-init @click="addFlow">
              <font-awesome-icon :icon="['fas', 'plus']" />Add new Flow
            </fts-btn>
          </div>
        </div> -->
        <!-- setting -->
        <div v-else-if="step === 'setting'">
          <div class="dm-group-label">Access &amp; listing</div>
          <div class="fts-row gutters">
            <!-- menus -->
            <div class="fts-col">
              <fts-form-element
                :formData="formData"
                :item="{
                  fieldName: 'menus',
                  displayName: 'Listing',
                  icon: 'fts-icon-menu-caret-down',
                  helper: 'To restrict access to specific listings for this activity.'
                }"
                comfort-design
              >
                <fts-select
                  slot="formElement"
                  slot-scope="{formData, item}"
                  :form-data="formData"
                  :item="item"
                  :options="menus"
                  multiple
                  comfort-design
                >
                  <fts-label-group
                    slot="selected-option-container"
                    slot-scope="{ option }"
                    :item-id="option.id"
                    :list="menus"
                    emoji
                  />
                  <fts-label-group
                    slot="option"
                    slot-scope="{ option }"
                    :item-id="option.id"
                    :list="menus"
                    emoji
                  />
                </fts-select>
              </fts-form-element>
            </div>
          </div>
          <fts-ledger-toggle-row
            :form-data="formData"
            field-name="secondary"
            label="Secondary"
            helper="Secondary level activity (alpha)"
            v-if="isInternal"
          />
          <fts-ledger-toggle-row
            :form-data="formData"
            field-name="viewOnly"
            label="View only"
            helper="View only activity (alpha)"
            v-if="isInternal"
          />
          <fts-ledger-toggle-row
            :form-data="formData"
            field-name="internal"
            label="For automation only"
          />
          <fts-ledger-toggle-row
            :form-data="formData"
            field-name="disableBulkActions"
            label="Bulk actions"
            off-label="Allowed"
            on-label="Disabled"
          />
          <div class="dm-group-label">After submit</div>
          <fts-activity-portal-row
            :form-data="formData"
            v-if="isInternal || formData.portalId"
          />
          <fts-ledger-toggle-row
            :form-data="formData"
            field-name="skipConfirm"
            label="Skip confirmation"
            helper="Skip the submit confirmation (alpha)"
            v-if="isInternal && !isFormDesign && !formData.portalId"
          />
          <div class="fts-row gutters">
            <!-- message -->
            <div class="fts-col" v-if="beta">
              <fts-form-element
                :form-data="formData"
                :item="{
                  type: 32,
                  displayName: 'Message',
                  fieldName: 'message',
                  helper: 'To show message after submission.'
                }"
                comfort-design
              >
                <template #formLabel="{ label }">
                  {{ label }} <fts-release-badge class="fts-align-top" />
                </template>
                <fts-formula
                  slot="formElement"
                  :template="true"
                  slot-scope="{ formData, item }"
                  :variables="variables"
                  v-model="formData[item.fieldName]"
                />
              </fts-form-element>
            </div>
            <!-- document -->
            <div class="fts-col fts-col-md-6" v-if="isInternal">
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'documentId',
                  displayName: 'Template',
                  icon: 'fts-icon-menu-caret-down',
                  helper: 'After submission, a template will be generated for preview. Currently only applicable to mobile devices.'
                }"
                comfort-design
              >
                <template #formLabel="{ label }">
                  {{label}} <fts-release-badge alpha class="fts-align-top" />
                </template>
                <fts-select
                  slot="formElement"
                  slot-scope="{ formData, item }"
                  :form-data="formData"
                  :item="item"
                  :options="documents"
                  comfort-design
                >
                  <fts-label-group
                    slot="selected-option-container"
                    slot-scope="{ option }"
                    :item-id="option.id"
                    :list="documents"
                    document
                  />
                  <fts-label-group
                    slot="option"
                    slot-scope="{ option }"
                    :item-id="option.id"
                    :list="documents"
                    document
                  />
                </fts-select>
              </fts-form-element>
            </div>
          </div>
          <div class="dm-group-label">History &amp; engine</div>
          <fts-ledger-toggle-row
            :form-data="formData"
            field-name="skipHistory"
            label="History"
            off-label="Recorded"
            on-label="Skipped"
          />
          <fts-server-native-row
            :activity="formData"
            :activity-id="formData.id"
            v-if="formData.design"
          />
          <div class="fts-row gutters">
            <!-- development -->
            <div class="fts-col fts-col-md-6" v-if="beta">
              <fts-form-element
                :formData="formData"
                :item="{
                  fieldName: 'development',
                  displayName: 'Development',
                  icon: 'fts-icon-menu-caret-down'
                }"
                comfort-design
              >
                <template #formLabel="{ label }">
                  {{label}} <fts-release-badge beta class="fts-align-top" />
                </template>
                <fts-select
                  slot="formElement"
                  slot-scope="{formData, item}"
                  :form-data="formData"
                  :item="item"
                  :options="[{ id: null, name: 'None' }, { id: 'dev', name: 'Development' }, { id: 'test', name: 'Testing' }, { id: 'internal', name: 'Internal' }]"
                  comfort-design
                ></fts-select>
              </fts-form-element>
            </div>
          </div>
        </div>
      </div>
      <!-- footer (temp) -->
      <div
        class="dm-stepper-footer"
        v-if="$v.formData && $v.formData.design && $v.formData.design.$error"
      >
        <div class="dm-stepper-footer-outer">
          <div
            class="dm-stepper-message dm-stepper-message--error"
          >Form cannot be empty. Form at least must have information or element.</div>
        </div>
      </div>
      <!-- state design modal -->
      <fts-state-design-modal ref="ftsStateDesignModal" />
    </template>
    <!-- footer:left -->
    <template #footerLeft>
      <!-- remove -->
      <fts-btn text icon @click="remove" v-if="isExist">
        <font-awesome-icon :icon="['far', 'trash-alt']" />
      </fts-btn>
      <!-- auth -->
      <fts-auth
        type="activity"
        :unit-id="formData.id"
        :title="formData.name"
        preview-placement="top"
      >
        <fts-btn
          slot="trigger"
          slot-scope="{ show, isRight }"
          text
          icon
          :color="isRight ? 'danger' : 'default'"
          @click="show"
        >
          <font-awesome-icon :icon="['fas', 'lock']" v-if="isRight" />
          <font-awesome-icon :icon="['fas', 'unlock']" v-else />
        </fts-btn>
      </fts-auth>
    </template>
    <!-- footer:right -->
    <template #footerRight>
      <!-- cancel -->
      <fts-btn text @click="beforeClose">Cancel</fts-btn>
      <!-- save -->
      <fts-btn color="primary" text @click="done(true)" v-if="isExist && Boolean(saveCallBack)">
        <font-awesome-icon :icon="['fas', 'check']" />Save
      </fts-btn>
      <!-- done -->
      <fts-btn outline color="primary" @click="done">
        <font-awesome-icon :icon="['fas', 'check']" />Ok
      </fts-btn>
      <!-- done and continue -->
      <fts-btn outline color="primary" @click="doneContinue" v-if="continuable">
        <font-awesome-icon :icon="['fas', 'chevron-right']" />Continue
      </fts-btn>
    </template>
  </fts-design-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);
import { required } from "vuelidate/lib/validators";

import Swatches from "vue-swatches";
import changesets from "diff-json";

import { authorizationStore } from "../store/authorizationStore";
import commons from "../commons/commons";
import canvasDesign from "../commons/canvasDesign";
import validator from "../commons/designValidator";
import { darkPalette } from "../commons/colors";

import FTSDesignModal from "./FTSDesignModal";
import FTSAuth from "./FTSAuth";
import FTSBtn from "../elements/FTSBtn";
import FTSCollapsibleElement from "../elements/FTSCollapsibleElement";
import FTSFormElement from "../elements/FTSFormElement";
import FTSEmoji from "../elements/FTSEmoji";
import FTSSelect from "../elements/FTSSelect";
import FTSLabelGroup from "../elements/FTSLabelGroup";
import FTSFormDesigner from "../elements/FTSFormDesigner";
import FTSTransitionExpand from "../elements/FTSTransitionExpand";
import FTSTippy from "../elements/FTSTippy";
import FTSContenteditable from "../elements/FTSContenteditable";
import FTSFormScript from "./FTSFormScript";
import FTSFormAutomationSection from "./FTSFormAutomationSection";
import FTSLedgerToggleRow from "./FTSLedgerToggleRow";
import FTSActivityPortalRow from "./FTSActivityPortalRow";
import FTSServerNativeRow from "./FTSServerNativeRow";
import FTSState from "../elements/FTSState";
import FTSStateDesignModal from "./FTSStateDesignModal";
import FTSStateDesignInline from "./FTSStateDesignInline";
import FTSFormula from "./FTSFormula";

export default {
  props: {
    builder: Boolean,
    isCapableWrite: {
      type: Boolean,
      default: true
    },
    isCapableWorkflow: {
      type: Boolean,
      default: true
    },
    isCapableAddInformation: {
      type: Boolean,
      default: true
    }
  },
  components: {
    Swatches,
    "fts-design-modal": FTSDesignModal,
    "fts-auth": FTSAuth,
    "fts-btn": FTSBtn,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-form-element": FTSFormElement,
    "fts-emoji": FTSEmoji,
    "fts-select": FTSSelect,
    "fts-label-group": FTSLabelGroup,
    "fts-form-designer": FTSFormDesigner,
    "fts-transition-expand": FTSTransitionExpand,
    "fts-tippy": FTSTippy,
    "fts-contenteditable": FTSContenteditable,
    "fts-form-script": FTSFormScript,
    "fts-form-automation-section": FTSFormAutomationSection,
    "fts-ledger-toggle-row": FTSLedgerToggleRow,
    "fts-activity-portal-row": FTSActivityPortalRow,
    "fts-server-native-row": FTSServerNativeRow,
    "fts-state": FTSState,
    "fts-state-design-modal": FTSStateDesignModal,
    "fts-state-design-inline": FTSStateDesignInline,
    "fts-formula": FTSFormula
  },
  data: () => ({
    step: "form",
    formData: null,
    originalData: null,
    flowFormData: null,
    resolve: null,
    saveCallBack: null,
    continueCallBack: null,
    alternativeDesignCallback: null,
    navigateCallback: null,
    params: null,
    autofillLoading: false,
    designNameHasFocus: false
  }),
  validations() {
    let formData = {};
    let flowFormData = {};
    formData = validator.activityDesign(this.activities);
    flowFormData = {
      fromStateId: { required },
      toStateId: { required }
    };
    return { formData, flowFormData };
  },
  methods: {
    async show(item, saveCallBack, continueCallBack, navigateCallback, params) {
      this.formData = _.cloneDeep(item);
      this.saveCallBack = saveCallBack;
      this.continueCallBack = continueCallBack;
      this.navigateCallback = navigateCallback;
      this.params = params;

      if (this.isExist) {
        const { t, tid } = this.$route.query;
        if (!t && !tid) {
          commons.addDesignQueryParams(this.$router, this.$route, [
            { name: "t", value: "activity" },
            { name: "tid", value: this.formData.id }
          ]);
        }
      }

      /* params.step */
      if (this.params && this.params.step && ['form', 'automation', 'script', 'ai', 'setting'].indexOf(this.params.step) > -1) {
        this.step = this.params.step;
      }
      /* flow from state */
      if (this.params && this.params.fromStateId) {
        this.addFlow();
        this.$set(this.flowFormData, "fromStateId", this.params.fromStateId);
      }
      /* form design */
      if (this.isFormDesign) {
        commons.expandWorkDesign(this.information, this.formData.design);
        // set payload
        if (!this.formData.design.payload)
          this.$set(this.formData.design, "payload", {});
        /* §4.3.3: rows saved before the seniority picker existed get the junior default so the
           select binds */
        if (Array.isArray(this.formData.design.aiActions)) {
          this.formData.design.aiActions.forEach(action => {
            if (action && !action.seniority) this.$set(action, "seniority", "junior");
          });
        }
      } else if (this.formData.design) {
        this.$delete(this.formData, "design");
      }

      return new Promise(resolve => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData);
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    }
                  }
                ]
              });
            } else {
              this.reset();
            }
          }
        });

        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
          this.$refs.designerName.focus();
          this.designNameHasFocus = true;
        });
      });
    },
    done(save, enableToast) {
      this.$v.formData.$touch();

      if (this.$v.formData.$invalid) {
        if (enableToast) {
          this.$toasted.error("Configuration error. Please ensure all values are in a proper format.");
        }
        return;
      }

      const formData = _.cloneDeep(this.formData);
      const alternativeDesign = this.getAlternativeDesign();
      if (save) {
        this.saveCallBack &&
          this.saveCallBack({ ...formData }, alternativeDesign);
        this.originalData = _.cloneDeep(this.formData);
        this.flowFormData = null;
      } else {
        this.resolve &&
          this.resolve({
            formData: formData,
            alternativeDesign: alternativeDesign
          });
        this.reset();
      }
    },
    doneContinue() {
      if (!this.continuable) return;

      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        const formData = _.cloneDeep(this.formData);
        const continueCallBack = Object.assign(this.continueCallBack);
        const alternativeDesign = this.getAlternativeDesign();
        /* reset */
        this.reset();
        continueCallBack && continueCallBack(formData, alternativeDesign);
        this.$toasted.info(`Activity: ${formData.name} is added.`);
      }
    },
    navigateContinue(type, item, params) {
      let alternativeDesign = null;

      function navigate(vm) {
        const formData = _.cloneDeep(vm.formData);
        const navigateCallback = Object.assign(vm.navigateCallback);
        /* reset */
        vm.reset();
        /* navigate */
        navigateCallback &&
          navigateCallback(formData, type, item, params, alternativeDesign);
      }
      /* set alternative design */
      alternativeDesign = this.getAlternativeDesign();

      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        if (this.isExist) {
          const diff = changesets.diff(this.formData, this.originalData);
          if (diff && diff.length) {
            this.$modal.show("dialog", {
              title: "Confirmation",
              text: "Keep changes and continue?",
              buttons: [
                { title: "Cancel" },
                {
                  title: "Continue",
                  default: true,
                  handler: () => {
                    this.$modal.hide("dialog");
                    navigate(this);
                  }
                }
              ]
            });
          } else {
            navigate(this);
          }
        } else {
          navigate(this);
        }
      }
    },
    beforeClose() {
      this.$refs.ftsDesignModal.beforeClose();
    },
    async remove() {
      const res = await this.$confirmation.show({
        type: "delete",
        title: "Activity",
        text: this.formData.name,
        textEmoji: this.formData.emoji,
        textColor: this.formData.color
      });

      if (res === true) {
        this.resolve && this.resolve(true);
        this.reset();
      }
    },
    reset() {
      const { t, tid } = this.$route.query || {};
      if (t === "activity" && tid === this.formData.id) {
        commons.removeDesignQueryParams(this.$router, this.$route, ["t", "tid"]);
      }

      this.$v.$reset();
      this.$refs.ftsDesignModal.close();
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    debounceInput: _.debounce(vm => {
      vm.autofillLoading = false;
    }, 800),
    dispatchAutofill() {
      if (!this.autofillLoading) this.autofillLoading = true;

      this.debounceInput(this);
    },
    error(key) {
      return validator.activityDesignError(this.$v.formData, key);
    },
    /* §4.3.3 form-level GNey actions */
    addAiAction() {
      const design = this.formData && this.formData.design;
      if (!design) return;
      if (!Array.isArray(design.aiActions)) {
        this.$set(design, "aiActions", []);
      }
      design.aiActions.push({
        id: commons.newUUID(),
        label: "",
        instruction: "",
        seniority: "junior"
      });
    },
    removeAiAction(action) {
      const design = this.formData && this.formData.design;
      if (!design || !Array.isArray(design.aiActions)) return;
      const index = design.aiActions.indexOf(action);
      if (index > -1) design.aiActions.splice(index, 1);
      if (!design.aiActions.length) this.$delete(design, "aiActions");
    },
    createWorkDesign() {
      this.$set(this.formData, "design", {
        rows: [{ index: 0, items: [] }],
        backendScript: "",
        payload: "{}"
      });
      this.$set(this.formData, "disableBulkActions", true);
      this.formData.skipConfirm && this.$set(this.formData, "skipConfirm", false);
    },
    deleteFormDesign() {
      this.$delete(this.formData, "design");
      this.$delete(this.formData, "disableBulkActions");
    },
    selectColor(val) {
      if (!val) this.$delete(this.formData, "color");
    },
    clearFlow() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: `Delete flow?`,
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.flowFormData = null;
            }
          }
        ]
      });
    },
    setFlow(item) {
      if (item && item.id) {
        let canvas = _.cloneDeep(this.$store.state.canvas);
        canvasDesign.setFlow(canvas, item);
        this.$store.dispatch("updateVectorCanvas", canvas);
      }
    },
    addFlow() {
      if (this.flowFormData && this.flowFormData.id) {
        this.$v.flowFormData.$touch();
        if (!this.$v.flowFormData.$invalid) {
          const submitData = _.cloneDeep(this.flowFormData);

          function continueFlow(vm) {
            /* set flow */
            vm.setFlow(submitData);
            /* reset */
            vm.$v.flowFormData.$reset();
            vm.flowFormData = {
              ...canvasDesign.initFlow(),
              activityId: vm.formData.id
            };
          }

          if (this.isExist) {
            this.$modal.show("dialog", {
              title: "Confirmation",
              text: "Submit new flow and continue?",
              buttons: [
                { title: "Cancel" },
                {
                  title: "Continue",
                  default: true,
                  handler: () => {
                    this.$modal.hide("dialog");
                    continueFlow(this);
                  }
                }
              ]
            });
          } else {
            continueFlow(this);
          }
        }
      } else {
        this.flowFormData = {
          ...canvasDesign.initFlow(),
          activityId: this.formData.id
        };
      }
    },
    removeFlow(item) {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: `Delete flow?`,
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              let canvas = _.cloneDeep(this.$store.state.canvas);
              canvasDesign.deleteFlow(canvas, item);
              this.$store.dispatch("updateVectorCanvas", canvas);
            }
          }
        ]
      });
    },
    navigateStateDesign(id) {
      const result = this.states.find(x => x.id === id);
      if (result) this.navigateContinue(canvasDesign.state, result);
      else this.$toasted.error("Something went wrong. State not found.");
    },
    navigateNewActivityDesign(id) {
      const params = { fromStateId: id, disableContinue: true };
      this.navigateContinue(canvasDesign.activity, null, params);
    },
    getAlternativeDesign() {
      let submitData = null;

      /* set flow data */
      if (this.flowFormData && this.flowFormData.fromStateId) {
        if (!submitData) submitData = [];

        let flow = _.cloneDeep(this.flowFormData);
        if (!flow.toStateId) flow.toStateId = flow.fromStateId;

        submitData.push({
          type: canvasDesign.flow.name,
          extension: flow
        });
      }

      return submitData;
    },
    openWorkflow() {
      this.$openStudioWorkflow && this.$openStudioWorkflow();
    },
    setStep(step) {
      this.step = step;
    },
  },
  watch: {
    /* a section can disappear (e.g. the form design is removed while its tab is open) —
       fall back to the first available one */
    navSections(list) {
      if (list.length && !list.some((t) => t.key === this.step)) {
        this.step = list[0].key;
      }
    }
  },
  computed: {
    /* top ledger nav (§3.2b): Form · Automation (form automation) · Script (beta) · Advanced */
    navSections() {
      if (!this.formData) return [];
      const tabs = [{ key: 'form', label: 'Form' }];
      if (this.formData.design) {
        tabs.push({ key: 'automation', label: 'Automation' });
        if (this.beta) tabs.push({ key: 'script', label: 'Script', beta: true });
      }
      tabs.push({ key: 'ai', label: 'AI' });
      tabs.push({ key: 'setting', label: 'Advanced' });
      return tabs;
    },
    /* the AI tab's confidence field speaks percent; the payload stays a 0-1 fraction
       (tolerating legacy already-scaled values, same as the annotation display) */
    aiConfidencePercent: {
      get() {
        const value = parseFloat(this.formData && this.formData.confidence_threshold);
        if (isNaN(value) || value <= 0) return "";
        return value <= 1 ? Math.round(value * 100) : Math.round(value);
      },
      set(val) {
        const percent = parseFloat(val);
        if (isNaN(percent) || percent <= 0) {
          this.$delete(this.formData, "confidence_threshold");
        } else {
          this.$set(this.formData, "confidence_threshold", Math.min(100, percent) / 100);
        }
      }
    },
    variables() {
      return [
        {
          name: "Entry",
          fieldName: "entry",
          type: 35,
          moduleId: this.$store.state.vectorId.toString()
        }
      ];
    },
    isDevelopment() {
      return authorizationStore.getters.isDevelopment;
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    /* §4.3.3 form-level GNey actions — the design's aiActions list (created on demand) */
    aiActions() {
      const design = this.formData && this.formData.design;
      return (design && design.aiActions) || [];
    },
    beta() {
      return (
        authorizationStore.getters.isBeta || this.$store.getters.workspaceBeta
      );
    },
    modules() {
      return this.$store.getters.workspaceModules;
    },
    information() {
      return this.$store.getters.canvasInformation;
    },
    activities() {
      return this.$store.getters.canvasActivities;
    },
    menus() {
      return this.$store.getters.canvasMenus.map(x => ({
        id: x.id,
        name: x.name,
        emoji: x.emoji
      }));
    },
    documents() {
      return this.$store.getters.canvasDocuments;
    },
    states() {
      return this.$store.getters.canvasStates;
    },
    colors() {
      return darkPalette.concat([""]);
    },
    isExist() {
      return this.activities.some(x => x.id === this.formData.id);
    },
    isFormDesign() {
      return Boolean(this.formData.design && this.formData.design.rows);
    },
    getStyle() {
      if (this.formData && this.formData.name && this.formData.color)
        return {
          color: this.formData.color
        };
    },
    continuable() {
      return (
        !this.isExist &&
        Boolean(this.params && this.params.disableContinue) !== true
      );
    },
    headerHintHelper() {
      return (
        this.continuable &&
        this.designNameHasFocus &&
        !this.autofillLoading &&
        !this.isExist &&
        this.formData &&
        this.formData.name &&
        this.$v.formData &&
        !this.$v.formData.$error
      );
    },
    activityFlows() {
      return this.$store.getters.canvasFlows.filter(
        x => x.activityId === this.formData.id
      );
    }
  }
};
</script>
<style lang="scss" scoped>
/* §4.3.3 form-level GNey actions editor */
.ai-actions-hint {
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
  opacity: 0.75;
}
.ai-actions-row {
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  margin-bottom: 0.375rem;
  .ai-actions-row__label {
    flex: 0 0 10rem;
  }
  .ai-actions-row__instruction {
    flex: 1;
  }
  .ai-actions-row__seniority {
    flex: 0 0 8.5rem;
  }
}
</style>